import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import DevExpress from 'devextreme';
import CustomStore from 'devextreme/data/custom_store';
import {BehaviorSubject, Observable} from 'rxjs';
import {retry} from 'rxjs/operators';
import {FusionAuthService} from '../../../shared/services/fusion-auth';
import {DateHelperService, UrlProviderService} from '../../../shared/services/general';
import {SECURED_STORE} from '../../../shared/utils';
import {Client} from '../models/order-entry/client';
import DataSource = DevExpress.data.DataSource;

@Injectable({
  providedIn: 'root'
})
export class ClientService {

  constructor(private urlProvider: UrlProviderService,
              private router: Router,
              private httpClient: HttpClient,
              private fusionAuthService: FusionAuthService,
              private dateHelper: DateHelperService) {
  }

  private customStore(dataSource: BehaviorSubject<DataSource>, loadUrl: string, params: any = null, key: string = 'id'): CustomStore {
    return SECURED_STORE({
      dataSource,
      loadUrl,
      routerService: this.router,
      tokenRefresh: this.fusionAuthService.exchangeRefreshTokenForJWT(),
      loadParams: params,
      key
    });
  }

  public advancedSearchStore(dataSource: BehaviorSubject<DataSource>, role: string, clientIds: number[]): CustomStore {
    return this.customStore(dataSource, `${this.urlProvider.ApiUrl}/v2/clients/advanced-search-clients`, {
      role,
      clientIds
    }, 'key');
  }

  public store(dataSource: BehaviorSubject<DataSource>) {
    return this.customStore(dataSource, `${this.urlProvider.ApiUrl}/v2/clients/remote-operations`);
  }

  public getByRange(orderId: number, startDate: Date, endDate: Date, limit: number, physicianIds: number[] = [], dataSource: BehaviorSubject<DataSource>) {
    let physicians = '';
    if (physicianIds && physicianIds.length > 0) {
      physicianIds.map(x => physicians += physicianIds.indexOf(x) === physicianIds.length - 1 ? `physicianIds=${x}` : `physicianIds=${x}&`);
    }
    const url = `${this.urlProvider.ApiUrl}/v2/clients/by-order-tests-in-time-range?orderId=${orderId}` +
      `&startDate=${this.dateHelper.formatDate(startDate)}&endDate=${this.dateHelper.formatDate(endDate)}&limit=${limit}` +
      `${physicianIds && physicianIds.length > 0 ? '&' + physicians : ''}`;
    return this.customStore(dataSource, url);
  }

  public customStoreFilteredByClientIds(ids: number[], dataSource: BehaviorSubject<DataSource>): CustomStore {
    let url = `${this.urlProvider.ApiUrl}/v2/clients/remote-operations`;
    if (ids.length > 0) {
      url += '?';
    }

    const clientIdsArray: string[] = [];
    ids.map(x => {
      clientIdsArray.push(`clientIds=${x}`);
    });

    if (clientIdsArray.length > 0) {
      url += clientIdsArray.join('&');
    }

    return this.customStore(dataSource, url);
  }

  public customStoreFilteredByPhysicianIds(ids: number[], dataSource: BehaviorSubject<DataSource>): CustomStore {
    let url = `${this.urlProvider.ApiUrl}/v2/clients/remote-operations`;

    if (ids.length > 0) {
      url += '?';
    }

    const patientIdsArray: string[] = [];
    ids.map(x => {
      patientIdsArray.push(`physicianIds=${x}`);
    });

    if (patientIdsArray.length > 0) {
      url += patientIdsArray.join('&');
    }

    return this.customStore(dataSource, url);
  }

  public customStoreFilteredByPatientIds(ids: number[], dataSource: BehaviorSubject<DataSource>): CustomStore {
    let url = `${this.urlProvider.ApiUrl}/v2/clients/remote-operations`;

    if (ids.length > 0) {
      url += '?';
    }

    const patientIdsArray: string[] = [];
    ids.map(x => {
      patientIdsArray.push(`patientIds=${x}`);
    });

    if (patientIdsArray.length > 0) {
      url += patientIdsArray.join('&');
    }

    return this.customStore(dataSource, url);
  }

  public clientUnitsCustomStore(id: number, dataSource: BehaviorSubject<DataSource>): CustomStore {
    return SECURED_STORE({
      loadUrl: `${this.urlProvider.ApiUrl}/v2/clients/${id}/units/remote-operations`,
      loadParams: undefined,
      tokenRefresh: this.fusionAuthService.exchangeRefreshTokenForJWT(),
      dataSource,
      routerService: this.router
    });
  }

  public clientRoomsCustomStore(id: number, dataSource: BehaviorSubject<DataSource>): CustomStore {
    return SECURED_STORE({
      loadUrl: `${this.urlProvider.ApiUrl}/v2/clients/${id}/rooms/remote-operations`,
      loadParams: undefined,
      tokenRefresh: this.fusionAuthService.exchangeRefreshTokenForJWT(),
      dataSource,
      routerService: this.router
    });
  }

  public clientRoomsByUnitCustomStore(id: number, dataSource: BehaviorSubject<DataSource>): CustomStore {
    return SECURED_STORE({
      loadUrl: `${this.urlProvider.ApiUrl}/v2/clients/units/${id}/rooms/remote-operations`,
      loadParams: undefined,
      tokenRefresh: this.fusionAuthService.exchangeRefreshTokenForJWT(),
      dataSource,
      routerService: this.router
    });
  }

  public getClientsByIds(clientIds: number[] = []): Observable<Client[]> {
    let url = `/v2/clients/by-ids?`;
    if (clientIds && clientIds.length > 0) {
      clientIds.map(x => url += `ids=${x}&`);
    }

    return this.httpClient.get<Client[]>(`${this.urlProvider.ApiUrl}${url}`)
      .pipe(retry(2));
  }
}
